import React from "react";
import { motion } from "framer-motion";
import { Search, ShieldCheck, Home } from "lucide-react";

const steps = [
  {
    title: "Choose a Service",
    desc: "Select the type of help you need in just a few simple steps.",
    icon: Search,
    tone: "indigo",
  },
  {
    title: "We Verify & Assign",
    desc: "We match you with trusted and verified professionals.",
    icon: ShieldCheck,
    tone: "emerald",
  },
  {
    title: "Get Help at Home",
    desc: "Sit back while the right support reaches your doorstep.",
    icon: Home,
    tone: "sky",
  },
];

const toneStyles = {
  indigo: {
    iconWrap:
      "from-indigo-50 to-violet-50 border-indigo-100 text-indigo-700",
    line: "from-indigo-500 to-violet-500",
    glow: "from-indigo-100/50 via-transparent to-violet-100/35",
    badge: "bg-indigo-50 text-indigo-700 border-indigo-100",
  },
  emerald: {
    iconWrap:
      "from-emerald-50 to-teal-50 border-emerald-100 text-emerald-700",
    line: "from-emerald-400 to-teal-500",
    glow: "from-emerald-100/50 via-transparent to-teal-100/35",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-100",
  },
  sky: {
    iconWrap: "from-sky-50 to-cyan-50 border-sky-100 text-sky-700",
    line: "from-sky-400 to-cyan-500",
    glow: "from-sky-100/50 via-transparent to-cyan-100/35",
    badge: "bg-sky-50 text-sky-700 border-sky-100",
  },
};

const HowItWorks = () => {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#f9f7ff_0%,#f2edff_42%,#fcfbff_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_18%,rgba(99,102,241,0.10),transparent_22%),radial-gradient(circle_at_84%_20%,rgba(168,85,247,0.08),transparent_20%),radial-gradient(circle_at_80%_82%,rgba(56,189,248,0.06),transparent_22%)]" />

      {/* Soft art */}
      <div className="absolute top-[-100px] left-[-80px] w-[260px] h-[260px] bg-purple-300/20 rounded-full blur-3xl" />
      <div className="absolute bottom-[-120px] right-[-80px] w-[280px] h-[280px] bg-indigo-300/20 rounded-full blur-3xl" />

      {/* Texture */}
      <div className="absolute inset-0 opacity-[0.035] [background-image:linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] [background-size:36px_36px]" />

      <div className="relative max-w-6xl mx-auto px-4">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-indigo-700 font-medium mb-5 shadow-sm">
            Simple Process
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            How It Works
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            A smooth, reliable process designed to help you get trusted support without unnecessary hassle.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            const tone = toneStyles[step.tone];

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 34 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="group relative overflow-hidden rounded-[28px] border border-white/80 bg-white/78 backdrop-blur-xl p-6 md:p-7 shadow-[0_14px_35px_rgba(15,23,42,0.06)] hover:shadow-[0_18px_42px_rgba(15,23,42,0.10)] transition-all duration-300 h-full flex flex-col"
              >
                {/* Hover glow */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-300 bg-gradient-to-br ${tone.glow} pointer-events-none`}
                />

                {/* Top accent line */}
                <div
                  className={`absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r ${tone.line}`}
                />

                {/* Top row */}
                <div className="relative flex items-start justify-between gap-3 mb-5">
                  <div
                    className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl border bg-gradient-to-br ${tone.iconWrap} flex items-center justify-center shadow-sm shrink-0`}
                  >
                    <Icon className="w-7 h-7" />
                  </div>

                  <span
                    className={`inline-flex items-center justify-center min-w-[40px] h-10 px-3 rounded-full text-sm font-semibold border ${tone.badge}`}
                  >
                    {i + 1}
                  </span>
                </div>

                {/* Title */}
                <h3 className="relative text-xl md:text-2xl font-semibold text-gray-900 mb-3">
                  {step.title}
                </h3>

                {/* Desc */}
                <p className="relative text-sm md:text-[15px] text-gray-600 leading-relaxed">
                  {step.desc}
                </p>

                {/* Bottom divider */}
                <div className="relative mt-auto pt-5">
                  <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
                </div>

                {/* Connector line desktop */}
                {i !== steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-[2px]">
                    <div
                      className={`w-full h-full bg-gradient-to-r ${tone.line}`}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;